import { Play } from '@tunasong/icons'
import { SUBDIVISION_POOL } from '@tunasong/music-lib'
import type { SubdivisionKind, SubdivisionQuestion } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { RhythmAnswerButton } from './answer-button.js'
import type { RhythmAnswerButtonState } from './answer-button.js'
import styles from './subdivision-question.module.css'

export interface SubdivisionQuestionPanelProps {
  question: SubdivisionQuestion
  scoreCorrect: number
  scoreAnswered: number
  interactive: boolean
  onHearIt: () => void
  onAnswer: (picked: SubdivisionKind) => void
}

/**
 * Rhythm Trainer Subdivision ID question panel. 4-button answer grid (8ths,
 * 16ths, triplets, swing). Same layout shape as `MeterQuestionPanel`; the
 * meter is fixed at 4/4 in the audition — only the subdivision varies.
 *
 * Spec: docs/superpowers/specs/2026-05-22-rhythm-trainer-v1-design.md §6.2
 */
export const SubdivisionQuestionPanel: FC<SubdivisionQuestionPanelProps> = props => {
  const { scoreCorrect, scoreAnswered, interactive, onHearIt, onAnswer } = props
  const buttonState: RhythmAnswerButtonState = interactive ? 'idle' : 'dimmed'
  return (
    <section className={styles.question} aria-label="Rhythm Trainer subdivision question">
      <header className={styles.header}>
        <span
          className={styles.scoreChip}
          aria-label={`${scoreCorrect} correct out of ${scoreAnswered} answered`}
        >
          {scoreCorrect} / {scoreAnswered}
        </span>
      </header>
      <p className={styles.prompt}>What subdivision did you hear?</p>
      <div className={styles.actionRow}>
        <Button variant="outlined" size="small" startIcon={<Play />} onClick={onHearIt}>
          Hear it
        </Button>
      </div>
      <div className={styles.cardGrid}>
        {SUBDIVISION_POOL.map(entry => (
          <RhythmAnswerButton
            key={entry.kind}
            pickValue={entry.kind}
            lexiconId={entry.lexiconId}
            fallbackLongName={entry.displayName}
            fallbackShortName={entry.displayNameShort}
            state={buttonState}
            onPick={picked => onAnswer(picked as SubdivisionKind)}
          />
        ))}
      </div>
    </section>
  )
}
